import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PencilSimple, Plugs, EnvelopeSimple, ChatText, ArrowSquareOut } from "@phosphor-icons/react";

const COPY = {
  sms: {
    title: "SMS Providers",
    subtitle: "Providers assigned by your administrator. Enter your own API keys to send SMS from this account.",
    empty: "No SMS providers assigned",
    emptyBody: "Ask your administrator to assign SMS providers on your account.",
    testid: "customer-sms-providers",
    Icon: ChatText,
  },
  email: {
    title: "Email Providers",
    subtitle: "Providers assigned by your administrator. Enter your own credentials to send email from this account.",
    empty: "No email providers assigned",
    emptyBody: "Ask your administrator to assign email providers on your account.",
    testid: "customer-email-providers",
    Icon: EnvelopeSimple,
  },
};

export default function CustomerProviders({ kind }) {
  const copy = COPY[kind] || COPY.sms;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ settings: {}, secrets: {} });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-providers", { params: { kind } });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => {
    setEdit(row);
    const settings = {};
    const secrets = {};
    (row.fields || []).forEach((f) => {
      if (f.secret) secrets[f.key] = "";
      else settings[f.key] = f.value || "";
    });
    setForm({ settings, secrets });
  };

  const save = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const settings = {};
      const secrets = {};
      (edit.fields || []).forEach((f) => {
        if (f.secret) {
          const v = (form.secrets?.[f.key] || "").trim();
          if (v) secrets[f.key] = v;
        } else {
          settings[f.key] = (form.settings?.[f.key] || "").trim();
        }
      });
      await api.patch(`/customer-providers/${edit.id}`, { settings, secrets });
      toast.success("Credentials saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Icon = copy.Icon;

  return (
    <div data-testid={copy.testid}>
      <PageHeader title={copy.title} subtitle={copy.subtitle} />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Icon}
          title={copy.empty}
          description={copy.emptyBody}
          testid={`${copy.testid}-empty`}
        />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Credentials</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid={`${copy.testid}-row-${row.slug}`}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-2">
                      <Plugs className="w-4 h-4 text-muted-foreground shrink-0" />
                      {row.name}
                    </div>
                    {row.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{row.description}</p>
                    )}
                    {row.website && (
                      <a
                        href={row.website}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-primary mt-0.5 hover:underline"
                      >
                        Docs <ArrowSquareOut className="w-3 h-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.has_credentials ? (
                      <span className="text-emerald-600">Configured</span>
                    ) : (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2"
                      onClick={() => openEdit(row)}
                      title="Configure credentials"
                      data-testid={`${copy.testid}-edit-${row.slug}`}
                    >
                      <PencilSimple className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${copy.testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Configure {edit?.name}</DialogTitle>
            <DialogDescription>
              Your credentials stay private to this account. Leave secret fields blank to keep the current value.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {(edit?.fields || []).map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>
                  {f.label}
                  {f.secret && f.mask && (
                    <span className="ml-2 text-muted-foreground font-normal">({f.mask})</span>
                  )}
                </Label>
                <Input
                  className="rounded-lg"
                  type={f.secret ? "password" : "text"}
                  autoComplete="off"
                  placeholder={f.secret ? (f.configured ? "Leave blank to keep current" : (f.placeholder || "")) : (f.placeholder || "")}
                  value={f.secret ? (form.secrets?.[f.key] || "") : (form.settings?.[f.key] || "")}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (f.secret) setForm({ ...form, secrets: { ...form.secrets, [f.key]: v } });
                    else setForm({ ...form, settings: { ...form.settings, [f.key]: v } });
                  }}
                  data-testid={`${copy.testid}-field-${f.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setEdit(null)}>Cancel</Button>
            <Button className="rounded-lg" disabled={saving} onClick={save} data-testid={`${copy.testid}-save`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
